<template>
  <v-card
    id="openings-enrollments"
    :border="$role == 'specialist'"
    tile
  >
    <v-card-title>
      <div class="d-flex">
        <v-btn
          v-if="showProviderProfileNavBtn"
          @click="emit('toggleProviderProfileNav')"
          :aria-label="$bct('Toggle provider profile navigation')"
          data-cy="provider_profile_nav_icon"
          icon="menu_open"
          variant="text"
        />
        <h2 class="v-card-title pa-0 d-flex align-center">
          {{ $bct('Openings & Enrollments') }}
        </h2>
      </div>
    </v-card-title>

    <v-card-text class="pb-0">
      {{ $bct(openingsAndEnrollmentsSubtitle) }}

      <v-row
        class="mt-2"
        style="min-height: 40px"
      >
        <!-- eslint-disable vue-router-rules/await-router-navigation -->
        <StandardButton
          v-if="showBackToLocationsBtn"
          @click="$router.push({ name: 'ProviderIndex' })"
          prepend-icon="arrow_back"
          role="link"
          text="Back to Locations"
        />

        <v-spacer />

        <template v-if="programs.length > 0">
          <LongDate
            v-if="openingsConfirmedAt"
            :date="openingsConfirmedAt"
            :no-translate-suffix="true"
            :suffix="confirmedBySuffix"
            class="me-4"
            date-classes="text-caption font-italic align-self-center me-4"
            prefix="Last confirmed"
          />
          <div
            v-else
            class="text-caption font-italic align-self-center me-4"
          >
            {{ $bct('Openings not confirmed') }}
          </div>
        </template>
      </v-row>
    </v-card-text>

    <v-row
      v-if="loading"
      class="pa-12"
    >
      <v-col class="d-flex justify-center">
        <v-progress-circular
          color="primary"
          size="50"
          indeterminate
        />
      </v-col>
    </v-row>
    <template v-else>
      <v-card-text class="pt-0">
        <v-alert
          v-if="activeErrors.length > 0"
          class="mt-2"
          density="compact"
          type="error"
          variant="tonal"
        >
          {{ activeErrors.join(', ') }}
        </v-alert>
        <v-card
          v-for="(_program, index) in programs"
          :key="index"
          class="mt-5"
          border
          tile
        >
          <template #text>
            <ProgramOpeningRow
              v-model="programs[index]"
              @change="programs[index].changed = true"
            />
          </template>
        </v-card>

        <template v-if="isManager">
          <v-row class="mt-2">
            <v-col
              class="d-flex justify-end"
              cols="12"
            >
              <StandardButton
                v-if="programs.length > 0"
                @click="showSummary"
                :full-width="$vuetify.display.smAndDown"
                :loading="processing"
                text="Review and confirm"
              />
            </v-col>
          </v-row>

          <TabButtons
            @back="emit('back')"
            @next="programs.length > 0 ? saveAndConfirmOpenings(true) : emit('next')"
            :processing="processing"
          />
        </template>
      </v-card-text>

      <v-card-actions
        v-if="isSpecialist && programs.length > 0"
        class="d-flex flex-wrap justify-end ga-2"
      >
        <StandardButton
          @click="showSummary"
          :full-width="$vuetify.display.smAndDown"
          :loading="processing"
          role="secondary"
          text="Show summary"
        />

        <StandardButton
          @click="saveAndConfirmOpenings"
          :full-width="$vuetify.display.smAndDown"
          :loading="processing"
          text="Save"
        />
      </v-card-actions>
    </template>
  </v-card>

  <OpeningsEnrollmentSummaryDialog
    @openings-confirmed="saveAndConfirmOpenings"
    ref="summaryDialog"
    :active-errors="activeErrors"
    :processing="processing"
    :programs="programs"
    :provider="provider"
  />
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import LongDate from '@/shared/components/LongDate.vue';
import OpeningsEnrollmentSummaryDialog from '@/shared/components/provider/OpeningsEnrollmentSummaryDialog.vue';
import ProgramOpeningRow from '@/shared/components/provider/availability/ProgramOpeningRow.vue';
import TabButtons from '@/manager/components/provider/TabButtons.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';
import useTerms from '@/shared/composables/useTerms';

const $bct = inject('translator');

const { api } = useApi();
const eventBus = useEventBus();
const { isManager, isSpecialist } = useRole();
const route = useRoute();
const store = useStore();
const { terms } = useTerms();

const props = defineProps({
  provider: {
    type: Object,
    default: null,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits(['toggleProviderProfileNav', 'back', 'next']);

const activeErrors = ref([]);
const loading = ref(true);
const openingsConfirmedAt = ref(props.provider.openings_confirmed_at);
const openingsConfirmedBy = ref(props.provider.openings_confirmed_by);
const processing = ref(false);
const programs = ref([]);
const summaryDialog = ref(null);

const confirmedBySuffix = computed(() => {
  if (!openingsConfirmedAt.value) return null;
  return `${$bct('by')} ${openingsConfirmedBy.value}`;
});

const openingsAndEnrollmentsSubtitle = computed(() => {
  if (loading.value) return;
  if (programs.value.length > 0) {
    return (
      'Keep your profile up to date by entering enrollment and opening counts for each program. ' +
      'Specify if openings are current or expected in the future, and use the optional note field ' +
      'to provide relevant internal details. If this information is already correct, click Review and confirm.'
    );
  }

  return `You do not have any ${terms.value.programs.toLowerCase()} to manage openings and enrollments for.`;
});

const showBackToLocationsBtn = computed(() => {
  return !store.state.site?.enable_provider_v2 && route.query.backTo === 'ProviderIndex';
});

async function saveAndConfirmOpenings(continueToNext = false) {
  processing.value = true;
  const hasSaveFailures = await savePrograms(continueToNext);
  if (hasSaveFailures) {
    processing.value = false;
    return; // Don't confirm openings if there are save errors
  }

  const response = await api.provider.update(props.provider.id, {
    openings_confirmed: true,
    openings_confirmed_by_id: store.state.profile.id,
  });
  openingsConfirmedAt.value = response.data.openings_confirmed_at;
  openingsConfirmedBy.value = response.data.openings_confirmed_by;
  summaryDialog.value.close();
  eventBus.chime('Openings confirmed');
  processing.value = false;
}

async function loadPrograms() {
  const response = await api.program.index(props.provider.id);
  programs.value = response?.data || [];
  loading.value = false;
}

async function savePrograms(continueToNext) {
  processing.value = true;
  const saveErrors = [];
  let hasFailures = false;

  const savePromises = programs.value.map((program) => {
    if (program.changed) {
      return saveProgram(program, (error) => {
        hasFailures = true;
        if (error.response?.data?.errors || error.response?.data?.error) {
          const errors = error.response.data.errors || error.response.data.error;
          const errorMessages = Array.isArray(errors) ? errors : [errors];
          saveErrors.push(...errorMessages);
        }
      });
    }
    return Promise.resolve();
  });

  await Promise.allSettled(savePromises);
  activeErrors.value = [...new Set(saveErrors)];
  if (activeErrors.value.length > 0 || hasFailures) {
    processing.value = false;
    return true;
  }

  programs.value.forEach((program) => (program.changed = false));

  if (isManager && continueToNext) emit('next');
  processing.value = false;
}

async function saveProgram(program, failure) {
  const params = {
    enrollments_count: program.enrollments_count || 0,
    openings_count: program.openings_count || 0,
    future_openings: program.future_openings,
    notes: program.notes,
  };

  await api.program.update(props.provider.id, program.id, params, null, failure);
}

function showSummary() {
  summaryDialog.value.open();
}

async function load() {
  await loadPrograms();
}

watch(
  () => props.provider,
  async () => {
    if (props.provider) {
      await load();
    }
  },
);

onMounted(load);
</script>
